/**
 * Runtime polyfills for old TV engines (Tizen 5–6.5, webOS 5–22 ≈ Chromium 63–87).
 * Syntax is lowered by the build (vite.config.ts `LEGACY_TARGET`); this module
 * covers the *APIs* newer than Chromium 63 that the app or its dependencies call.
 *
 * Rules: no npm dependencies, tiny implementations, every patch guarded so modern
 * engines run their native code untouched. It is imported first by main.tsx and
 * built as its own chunk so it evaluates before the vendor chunks (see vite.config).
 */

/* eslint-disable @typescript-eslint/no-explicit-any -- patching built-ins needs untyped access */

type AnyFn = (...args: any[]) => any;

function define(target: object, name: string, value: unknown): void {
  if (name in target) return;
  Object.defineProperty(target, name, { value, writable: true, configurable: true, enumerable: false });
}

/* ---------- globalThis (Chromium 71) ---------- */
const g: any =
  typeof globalThis !== 'undefined'
    ? globalThis
    : typeof self !== 'undefined'
      ? self
      : typeof window !== 'undefined'
        ? window
        : Function('return this')();
if (typeof globalThis === 'undefined') g.globalThis = g;

/* ---------- vite.config `legacyImportMeta`: URL lazy chunks resolve against ---------- */
if (typeof document !== 'undefined' && !g.__AURORA_U) {
  const entry = document.querySelector<HTMLScriptElement>('script[type="module"][src]');
  g.__AURORA_U = entry?.src || document.baseURI;
}

/* ---------- queueMicrotask (71) ---------- */
if (typeof g.queueMicrotask !== 'function') {
  g.queueMicrotask = (cb: () => void) => {
    Promise.resolve()
      .then(cb)
      .catch((e: unknown) =>
        setTimeout(() => {
          throw e;
        }, 0),
      );
  };
}

/* ---------- Promise.allSettled (76) ---------- */
define(Promise, 'allSettled', function allSettled<T>(items: Iterable<T | PromiseLike<T>>) {
  return Promise.all(
    Array.from(items, (item) =>
      Promise.resolve(item).then(
        (value) => ({ status: 'fulfilled' as const, value }),
        (reason: unknown) => ({ status: 'rejected' as const, reason }),
      ),
    ),
  );
});

/* ---------- Array.prototype.flat / flatMap (69), at (92) ---------- */
function flatten(source: readonly unknown[], depth: number, out: unknown[]): unknown[] {
  for (let i = 0; i < source.length; i++) {
    if (!(i in source)) continue;
    const v = source[i];
    if (depth > 0 && Array.isArray(v)) flatten(v, depth - 1, out);
    else out.push(v);
  }
  return out;
}
define(Array.prototype, 'flat', function flat(this: unknown[], depth?: number) {
  return flatten(this, depth === undefined ? 1 : Number(depth) || 0, []);
});
define(Array.prototype, 'flatMap', function flatMap(this: unknown[], fn: AnyFn, thisArg?: unknown) {
  return flatten(this.map(fn, thisArg), 1, []);
});
const at = function at(this: ArrayLike<unknown>, index: number) {
  const n = Math.trunc(Number(index)) || 0;
  const i = n < 0 ? this.length + n : n;
  return i < 0 || i >= this.length ? undefined : this[i];
};
define(Array.prototype, 'at', at);
define(String.prototype, 'at', at);

/* ---------- Object.fromEntries (73), Object.hasOwn (93) ---------- */
define(Object, 'fromEntries', function fromEntries(entries: Iterable<readonly [PropertyKey, unknown]>) {
  const out: Record<PropertyKey, unknown> = {};
  for (const [k, v] of entries) out[k] = v;
  return out;
});
define(Object, 'hasOwn', function hasOwn(obj: object, key: PropertyKey) {
  return Object.prototype.hasOwnProperty.call(obj, key);
});

/* ---------- String.prototype.replaceAll (85) ---------- */
define(String.prototype, 'replaceAll', function replaceAll(this: string, search: string | RegExp, replacement: any) {
  if (search instanceof RegExp) {
    if (!search.global) throw new TypeError('replaceAll must be called with a global RegExp');
    return this.replace(search, replacement);
  }
  return this.split(String(search)).join(typeof replacement === 'function' ? String(replacement(search)) : String(replacement));
});

/* ---------- AbortController (66) — fetch honours the polyfilled signal ---------- */
if (typeof g.AbortController !== 'function') {
  type Listener = (e: { type: string }) => void;
  class AbortSignalShim {
    aborted = false;
    reason: unknown = undefined;
    onabort: Listener | null = null;
    private listeners: Listener[] = [];
    addEventListener(type: string, fn: Listener) {
      if (type === 'abort') this.listeners.push(fn);
    }
    removeEventListener(type: string, fn: Listener) {
      if (type === 'abort') this.listeners = this.listeners.filter((l) => l !== fn);
    }
    throwIfAborted() {
      if (this.aborted) throw this.reason;
    }
    /** @internal */
    fire(reason: unknown) {
      if (this.aborted) return;
      this.aborted = true;
      this.reason = reason;
      const ev = { type: 'abort' };
      this.onabort?.(ev);
      for (const l of this.listeners.slice()) l(ev);
    }
  }
  class AbortControllerShim {
    readonly signal = new AbortSignalShim();
    abort(reason?: unknown) {
      this.signal.fire(reason ?? abortError());
    }
  }
  g.AbortController = AbortControllerShim;
  g.AbortSignal = AbortSignalShim;

  const nativeFetch: typeof fetch | undefined = g.fetch;
  if (nativeFetch) {
    g.fetch = (input: RequestInfo, init?: RequestInit & { signal?: any }) => {
      const signal = init?.signal as AbortSignalShim | undefined;
      if (!(signal instanceof AbortSignalShim)) return nativeFetch(input, init);
      if (signal.aborted) return Promise.reject(signal.reason);
      const rest = { ...init };
      delete rest.signal;
      return new Promise<Response>((resolve, reject) => {
        const onAbort = () => reject(signal.reason);
        signal.addEventListener('abort', onAbort);
        nativeFetch(input, rest).then(resolve, reject).finally(() => signal.removeEventListener('abort', onAbort));
      });
    };
  }
}

function abortError(): Error {
  try {
    return new DOMException('The operation was aborted.', 'AbortError');
  } catch {
    const e = new Error('The operation was aborted.');
    e.name = 'AbortError';
    return e;
  }
}

/* ---------- ResizeObserver (64) — coarse: window resize + a slow size poll ---------- */
if (typeof g.ResizeObserver !== 'function' && typeof window !== 'undefined') {
  type Entry = { target: Element; contentRect: { width: number; height: number; top: number; left: number } };
  const observers = new Set<ResizeObserverShim>();
  let timer = 0;
  const tick = () => observers.forEach((o) => o.check());
  const sync = () => {
    if (observers.size && !timer) {
      timer = window.setInterval(tick, 300);
      window.addEventListener('resize', tick);
    } else if (!observers.size && timer) {
      window.clearInterval(timer);
      timer = 0;
      window.removeEventListener('resize', tick);
    }
  };
  class ResizeObserverShim {
    private sizes = new Map<Element, string>();
    constructor(private readonly cb: (entries: Entry[], observer: ResizeObserverShim) => void) {}
    observe(target: Element) {
      this.sizes.set(target, '');
      observers.add(this);
      sync();
      // Native observers report the initial size asynchronously.
      g.queueMicrotask(() => this.check());
    }
    unobserve(target: Element) {
      this.sizes.delete(target);
      if (!this.sizes.size) this.disconnect();
    }
    disconnect() {
      this.sizes.clear();
      observers.delete(this);
      sync();
    }
    check() {
      const entries: Entry[] = [];
      this.sizes.forEach((last, target) => {
        const el = target as HTMLElement;
        const width = el.clientWidth;
        const height = el.clientHeight;
        const key = `${width}x${height}`;
        if (key === last) return;
        this.sizes.set(target, key);
        entries.push({ target, contentRect: { width, height, top: 0, left: 0 } });
      });
      if (entries.length) this.cb(entries, this);
    }
  }
  g.ResizeObserver = ResizeObserverShim;
}

export {};
